const LOAD = 'twineApp/notifications/LOAD';
const LOAD_SUCCESS = 'twineApp/notifications/LOAD_SUCCESS';
const LOAD_FAIL = 'twineApp/notifications/LOAD_FAIL';
const LOADPREVIEW = 'twineApp/notifications/LOADPREVIEW';
const LOADPREVIEW_SUCCESS = 'twineApp/notifications/LOADPREVIEW_SUCCESS';
const LOADPREVIEW_FAIL = 'twineApp/notifications/LOADPREVIEW_FAIL';
const DELETE = 'twineApp/notifications/DELETE';
const DELETE_SUCCESS = 'twineApp/notifications/DELETE_SUCCESS';
const DELETE_FAIL = 'twineApp/notifications/DELETE_FAIL';
const MARKASREAD = 'twineApp/notifications/MARKASREAD';
const MARKASREAD_SUCCESS = 'twineApp/notifications/MARKASREAD_SUCCESS';
const MARKASREAD_FAIL = 'twineApp/notifications/MARKASREAD_FAIL';
const MARKALLREAD = 'twineApp/notifications/MARKALLREAD';
const MARKALLREAD_SUCCESS = 'twineApp/notifications/MARKALLREAD_SUCCESS';
const MARKALLREAD_FAIL = 'twineApp/notifications/MARKALLREAD_FAIL';
const LOADCOUNT = 'twineApp/notifications/LOADCOUNT';
const LOADCOUNT_SUCCESS = 'twineApp/notifications/LOADCOUNT_SUCCESS';
const LOADCOUNT_FAIL = 'twineApp/notifications/LOADCOUNT_FAIL';

export const PAGE_SIZE = 20;
export const PREVIEW_SIZE = 5;

const INCLUDES =
  'include=notifiable,notifiable.notice,notifiable.sender,notifiable.user';

const initialState = {
  loaded: false,
  loading: false,
  notifications: [],
  moreNotifications: false,
  notificationsPagesLoaded: 0,
  unreadOnly: false,
  preview: [],
  previewLoaded: false,
  previewLoading: false,
  count: 0,
  loadingCount: false,
  loadedCount: false,
};

// The API sends read_at as '' when a notification is unread
export const isUnread = (notification) => !notification.read_at;

const markRead = (list, id, readAt) =>
  list.map((notification) =>
    notification.id === id && isUnread(notification)
      ? { ...notification, read_at: readAt }
      : notification
  );

const markAllRead = (list, readAt) =>
  list.map((notification) =>
    isUnread(notification) ? { ...notification, read_at: readAt } : notification
  );

// Read items no longer belong in the unread-only list
const withoutReadIfUnreadOnly = (state, list) =>
  state.unreadOnly ? list.filter(isUnread) : list;

const findNotification = (state, id) =>
  state.notifications.find((notification) => notification.id === id) ||
  state.preview.find((notification) => notification.id === id);

const resultNotifications = (result) => (result && result.notifications) || [];

const hasMorePages = (result) => {
  const pagination = result && result.meta && result.meta.pagination;

  return pagination ? pagination.current_page < pagination.total_pages : false;
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case LOAD:
      return {
        ...state,
        loading: true,
        unreadOnly: action.unreadOnly,
        // Switching filter starts a fresh list
        ...(action.page === 1 && action.unreadOnly !== state.unreadOnly
          ? { notifications: [], notificationsPagesLoaded: 0, loaded: false }
          : {}),
      };

    case LOAD_SUCCESS: {
      // A response for a filter the user has since switched away from
      if (action.unreadOnly !== state.unreadOnly) {
        return state;
      }

      const incoming = resultNotifications(action.result);
      const existing =
        action.page === 1
          ? []
          : state.notifications.filter(
              (notification) =>
                !incoming.some((item) => item.id === notification.id)
            );

      return {
        ...state,
        loaded: true,
        loading: false,
        notifications: existing.concat(incoming),
        moreNotifications: hasMorePages(action.result),
        notificationsPagesLoaded: action.page,
      };
    }

    case LOAD_FAIL:
      return action.unreadOnly !== state.unreadOnly
        ? state
        : {
            ...state,
            loading: false,
          };

    case LOADPREVIEW:
      return {
        ...state,
        previewLoading: true,
      };

    case LOADPREVIEW_SUCCESS:
      return {
        ...state,
        previewLoading: false,
        previewLoaded: true,
        preview: resultNotifications(action.result),
      };

    case LOADPREVIEW_FAIL:
      return {
        ...state,
        previewLoading: false,
      };

    case DELETE: {
      // Eager delete
      const deleted = findNotification(state, action.id);

      return {
        ...state,
        notifications: state.notifications.filter(
          (notification) => notification.id !== action.id
        ),
        preview: state.preview.filter(
          (notification) => notification.id !== action.id
        ),
        count:
          deleted && isUnread(deleted)
            ? Math.max(state.count - 1, 0)
            : state.count,
      };
    }

    case MARKASREAD: {
      // Eager mark as read
      const notification = findNotification(state, action.id);

      if (!notification || !isUnread(notification)) {
        return state;
      }

      return {
        ...state,
        notifications: withoutReadIfUnreadOnly(
          state,
          markRead(state.notifications, action.id, action.readAt)
        ),
        preview: markRead(state.preview, action.id, action.readAt),
        count: Math.max(state.count - 1, 0),
      };
    }

    case MARKALLREAD:
      // Eager mark all as read
      return {
        ...state,
        notifications: withoutReadIfUnreadOnly(
          state,
          markAllRead(state.notifications, action.readAt)
        ),
        preview: markAllRead(state.preview, action.readAt),
        count: 0,
      };

    case LOADCOUNT:
      return {
        ...state,
        loadingCount: true,
      };

    case LOADCOUNT_SUCCESS:
      return {
        ...state,
        loadingCount: false,
        loadedCount: true,
        count: action.result.count,
      };

    case LOADCOUNT_FAIL:
      return {
        ...state,
        loadingCount: false,
      };

    case DELETE_SUCCESS:
    case DELETE_FAIL:
    case MARKASREAD_SUCCESS:
    case MARKASREAD_FAIL:
    case MARKALLREAD_SUCCESS:
    case MARKALLREAD_FAIL:
    default:
      return state;
  }
}

export function isLoaded(globalState) {
  return globalState.notifications && globalState.notifications.loaded;
}

export function load(page, unreadOnly = false) {
  return {
    types: [LOAD, LOAD_SUCCESS, LOAD_FAIL],
    promise: (client) =>
      client.get(
        `/notifications?page=${page}&limit=${PAGE_SIZE}${
          unreadOnly ? '&unread=1' : ''
        }&${INCLUDES}`
      ),
    page,
    unreadOnly,
  };
}

export function loadPreview() {
  return {
    types: [LOADPREVIEW, LOADPREVIEW_SUCCESS, LOADPREVIEW_FAIL],
    promise: (client) =>
      client.get(`/notifications?page=1&limit=${PREVIEW_SIZE}&${INCLUDES}`),
  };
}

export function loadCount() {
  return {
    types: [LOADCOUNT, LOADCOUNT_SUCCESS, LOADCOUNT_FAIL],
    promise: (client) => client.get('/notifications/count'),
  };
}

// Each change re-syncs the badge count with the server once it lands, in case
// the eager update was wrong (e.g. the notification was already read elsewhere).
// A failed change also reloads the preview and list to undo the eager update.
const thenReloadCount = (dispatch, getState, promise) =>
  promise.then(
    () => dispatch(loadCount()),
    () => {
      dispatch(load(1, getState().notifications.unreadOnly));
      dispatch(loadPreview());
      return dispatch(loadCount());
    }
  );

export function deleteNotification(id) {
  return (dispatch, getState) =>
    thenReloadCount(
      dispatch,
      getState,
      dispatch({
        types: [DELETE, DELETE_SUCCESS, DELETE_FAIL],
        promise: (client) => client.delete('/notifications/' + id),
        id,
      })
    );
}

export function markAsRead(id) {
  return (dispatch, getState) =>
    thenReloadCount(
      dispatch,
      getState,
      dispatch({
        types: [MARKASREAD, MARKASREAD_SUCCESS, MARKASREAD_FAIL],
        promise: (client) => client.put('/notifications/' + id + '/read'),
        id,
        readAt: new Date().toISOString(),
      })
    );
}

export function markAllAsRead() {
  return (dispatch, getState) =>
    thenReloadCount(
      dispatch,
      getState,
      dispatch({
        types: [MARKALLREAD, MARKALLREAD_SUCCESS, MARKALLREAD_FAIL],
        promise: (client) => client.put('/notifications/read-all'),
        readAt: new Date().toISOString(),
      })
    );
}
